import { get, once } from 'lodash';
import { errors } from '@paychex/core';

import type { Adapter, DataLayer, DataProxy } from '@paychex/core/types/data';
import { AutoResetSignal } from '@paychex/core/types/signals';
import { CrossOriginEventBus } from '@paychex/platform-browser/types/crossOrigin';
import { OIDC } from './index';

const metadata = {
    base: 'oidc',
    method: 'GET',
    path: '/.well-known/openid-configuration',
    headers: {
        accept: 'application/json'
    }
}

function urlencoded(payload: Record<string, any>) {
    return new URLSearchParams(payload);
}

function assign(out: Record<string, any>, [key, value]: [string, any]) {
    out[key] = value;
    return out;
}

interface CacheData {
    expireTimeout: any,
    refreshToken: string,
    expirationTime: number,
}

interface FactoryOptions {
    xhr: Adapter,
    data: {
        createProxy: () => DataProxy,
        createDataLayer: (proxy: DataProxy, adapter: Adapter) => DataLayer,
        utils: { tokenize: (...args: any[]) => string },
    },
    signals: { autoReset: (signaled: boolean) => AutoResetSignal },
    crossOrigin: { bus: (options: Record<string, any>) => CrossOriginEventBus },
    BUFFER?: number,
}

interface OIDCResult {
    state: number,
    refresh_token: string,
    expires_in: number,
    error?: string,
}

export default function factory(options: FactoryOptions) {

    const {
        xhr,
        data,
        signals,
        crossOrigin,
    } = options;

    // seconds before expiration that a refresh should be issued
    const BUFFER = options.BUFFER || 60;

    function mutex(fn: CallableFunction) {
        const signal = signals.autoReset(true);
        return async function queued(...args: any[]) {
            try {
                await signal.ready();
                // we need to wait on the promise
                // before returning; otherwise,
                // we will enter the finally block
                // early, allowing the critical
                // region to run concurrently
                return await (fn as any).apply(this, args);
            } finally {
                signal.set();
            }
        };
    }

    return async function create(authority_url: string, client_id: string, iframe_uri: string, verifier: string, challenge: string, audience: string, connection: string, with_creds: string, cache = new Map()): Promise<OIDC> {

        let bus: CrossOriginEventBus;
        const internal = new Map<string, CacheData>();

        const proxy = data.createProxy();
        const url = new URL(authority_url);
        const { fetch, createRequest } = data.createDataLayer(proxy, xhr);

        const getOIDCMetadata = once(async function loadMetadata() {
            const request = createRequest(metadata);
            const response = await fetch(request);
            return response.data;
        });

        proxy.use({
            host: url.hostname,
            protocol: url.protocol,
            port: Number(url.port || 80),
            match: {
                base: 'oidc'
            }
        });

        function disposeToken(auth: string) {
            cache.clear();
            internal.delete(auth);
        }

        function cacheToken(auth: string, result: OIDCResult) {
            const data = internal.get(auth);
            const expires_sec = result.expires_in;
            const expires_ms = ((expires_sec * 1000) - (BUFFER * 1000));
            clearTimeout(data.expireTimeout);
            cache.set(auth, result);
            if (expires_sec)
                data.expireTimeout =
                    setTimeout(disposeToken, expires_ms, auth);
            if (result.refresh_token) {
                setUpRefresh(expires_sec, refresh, auth, result.refresh_token);
            }
            else {
                data.expirationTime = Date.now() + (result.expires_in * 1000);
            }
        }

        function alreadyInvoked(auth: string) {
            return internal.has(auth);
        }

        function isExpired(auth: string) {
            return alreadyInvoked(auth) && Date.now() >= internal.get(auth).expirationTime;
        }

        function setUpRefresh(
            expires_in: number,
            fn: TimerHandler,
            key: string,
            refresh_token: string
        ) {
            const data = internal.get(key);
            const timeout = (expires_in - BUFFER) * 1000;
            data.refreshToken = refresh_token;
            data.expirationTime = Date.now() + (expires_in * 1000);
            if (timeout > 0)
                setTimeout(fn, timeout, key, refresh_token);
        }

        function redirect(endpoint: string): Promise<OIDCResult> {
            return new Promise((resolve) => {
                if (bus) {
                    bus.one('PARAMS_UPDATED', resolve);
                    bus.fire('GET_UPDATED_PARAMS', endpoint);
                } else {
                    bus = crossOrigin.bus({
                        key: 'oidc',
                        url: endpoint,
                        origins: ['*.paychex.com', '*.payxdev.com'],
                    });
                    bus.one('PARAMS_UPDATED', resolve);
                }
            });
        }

        async function form(action: string, body: any): Promise<OIDCResult> {
            const location = new URL(action);
            const params = Array.from(location.searchParams.entries())
                .reduce(assign, Object.create(null));
            let operation = createRequest({
                base: 'oidc',
                method: 'POST',
                path: location.pathname,
                headers: {
                    'content-type': 'application/x-www-form-urlencoded'
                }
            }, params, body);

            if(with_creds === 'true'){
                operation = createRequest({
                    base: 'oidc',
                    method: 'POST',
                    path: location.pathname,
                    withCredentials: true,
                    headers: {
                    'content-type': 'application/x-www-form-urlencoded'
                    }
                }, params, body);
            }

            const response = await fetch(operation);
            return response.data;
        }

        async function authorize(scopes: string[] = [], impersonate?: string, generatedCodeChallenge?: string) {

            const key = `authcode:${impersonate || 'user'}`;

            if(cache.has(key)){
                try {
                    const auth = cache.get(key).code;
                    const data = internal.get(auth)
                    if (!data.refreshToken) {
                        if (isExpired(auth)) {
                            disposeToken(auth)
                        }
                    }
                } catch (e) {
                    //do-nothing
                }
            }
            if (!cache.has(key)) {
                const oidc = await getOIDCMetadata();
                const state = Math.round(Math.random() * 1e6);
                const automationFlag = (get(window, 'jspVars.isPXT') === 'true' || get(window, 'jspVars.isTAF') === 'true');
                const endpoint = data.utils.tokenize(oidc.authorization_endpoint, {
                    state,
                    client_id,
                    prompt: 'none',
                    response_type: 'code',
                    redirect_uri: iframe_uri,
                    code_challenge: generatedCodeChallenge,
                    code_challenge_method: 'S256',
                    flexuser: impersonate,
                    audience: audience,
                    connection: connection,
                    automation: automationFlag,
                    scope: scopes.sort().join(' '),
                });
                const result = await redirect(endpoint);
                if (result.state !== state)
                    throw errors.error('Invalid state received from OIDC', { tags: ['security'] });
                else if (result.error)
                    throw errors.error(result.error, errors.fatal());
                cache.set(key, result);
            }
            return cache.get(key).code;
        }

        async function token(auth: string, generatedVerifier?: string) {
            if (isExpired(auth)) {
                // refresh token and update cache
                const data = internal.get(auth);
                await refresh(auth, data.refreshToken);
            }
            if (!cache.has(auth)) {
                if (alreadyInvoked(auth))
                    throw errors.error('Token called a second time, not from refresh.', errors.fatal());
                internal.set(auth, {
                    expireTimeout: null,
                    refreshToken: null,
                    expirationTime: Number.MAX_VALUE,
                });
                const params = {
                    client_id,
                    code: auth,
                    redirect_uri: iframe_uri,
                    code_verifier: generatedVerifier,
                    grant_type: 'authorization_code',
                };
                const oidc = await getOIDCMetadata();
                const result = await form(oidc.token_endpoint, urlencoded(params));
                cacheToken(auth, result);
            }
            return cache.get(auth).access_token;
        }

        async function refresh(auth: string, refresh_token: string) {
            const params = {
                client_id,
                refresh_token,
                grant_type: 'refresh_token',
            };
            const oidc = await getOIDCMetadata();
            const result = await form(oidc.token_endpoint, urlencoded(params));
            cacheToken(auth, result);
        }

        return {
            token: mutex(token),
            authorize: mutex(authorize),
        };

    };

}
